import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const { currentTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: currentTheme.background }}
    >
      <div className="w-full max-w-md">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 mb-8 font-body opacity-70 hover:opacity-100 transition-opacity"
          style={{ color: currentTheme.foreground }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>

        <div 
          className="rounded-2xl p-8"
          style={{ backgroundColor: 'white', boxShadow: '0 4px 30px rgba(0,0,0,0.08)' }}
        >
          {success ? (
            <div className="text-center py-8">
              <div 
                className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                style={{ backgroundColor: `${currentTheme.primary}15` }}
              >
                <CheckCircle className="w-8 h-8" style={{ color: currentTheme.primary }} />
              </div>
              <h1 className="font-display text-2xl font-medium mb-3" style={{ color: currentTheme.foreground }}>
                Password Reset!
              </h1>
              <p className="font-body opacity-70 mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Link to="/login">
                <Button className="w-full h-12" style={{ backgroundColor: currentTheme.primary }}>
                  Go to Login
                </Button>
              </Link>
            </div>
          ) : error && !token ? (
            <div className="text-center py-8">
              <div 
                className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                style={{ backgroundColor: '#FEE2E2' }}
              >
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="font-display text-2xl font-medium mb-3" style={{ color: currentTheme.foreground }}>
                Invalid Link
              </h1>
              <p className="font-body opacity-70 mb-6">
                {error}
              </p>
              <Link to="/forgot-password">
                <Button className="w-full h-12" style={{ backgroundColor: currentTheme.primary }}>
                  Request New Reset Link
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div 
                  className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: `${currentTheme.primary}15` }}
                >
                  <Lock className="w-7 h-7" style={{ color: currentTheme.primary }} />
                </div>
                <h1 className="font-display text-2xl font-medium" style={{ color: currentTheme.foreground }}>
                  Reset Password
                </h1>
                <p className="font-body opacity-70 mt-2">
                  Enter your new password below.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label className="font-body font-medium">New Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      data-testid="reset-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs opacity-60 font-body">Minimum 6 characters</p>
                </div>

                <div className="space-y-2">
                  <Label className="font-body font-medium">Confirm Password</Label>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="reset-confirm-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12"
                  style={{ backgroundColor: currentTheme.primary }}
                  disabled={loading || !newPassword || !confirmPassword}
                  data-testid="reset-password-submit"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
